import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { API } from "@/lib/admin-constants";
import {
  Link2, Unlink, RefreshCw, Users, ArrowRight, Loader2,
  Download, Upload, ExternalLink, ChevronDown, ChevronRight,
  Check, AlertCircle, UserPlus, Search,
} from "lucide-react";

const TeamSnapManager = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [rosters, setRosters] = useState({});
  const [rosterLoading, setRosterLoading] = useState({});
  const [registrationLinks, setRegistrationLinks] = useState([]);
  const [lastSync, setLastSync] = useState(null);
  const [cfDiscovering, setCfDiscovering] = useState(false);
  const [cfResult, setCfResult] = useState(null);
  const [job, setJob] = useState(null);
  const [jobHistory, setJobHistory] = useState([]);
  const [deepEnrich, setDeepEnrich] = useState(true);
  const [visibleTeams, setVisibleTeams] = useState(null);
  const [scheduleTeams, setScheduleTeams] = useState([]);
  const [savingVisibility, setSavingVisibility] = useState(false);

  const fetchVisibility = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/schedule/visibility`, { withCredentials: true });
      setScheduleTeams(res.data.teams || []);
      setVisibleTeams(res.data.visible);
    } catch { /* noop */ }
  }, []);

  const toggleVisible = (teamName) => {
    setVisibleTeams((prev) => {
      const current = prev === null ? scheduleTeams.map((t) => t.team) : prev;
      return current.includes(teamName) ? current.filter((t) => t !== teamName) : [...current, teamName];
    });
  };

  const saveVisibility = async (override) => {
    const payload = override !== undefined ? override : visibleTeams;
    setSavingVisibility(true);
    try {
      const res = await axios.put(`${API}/schedule/visibility`, { visible: payload }, { withCredentials: true });
      setVisibleTeams(res.data.visible);
      toast.success(res.data.showing_all ? "All teams are visible to parents" : `${payload.length} team(s) visible on the Parent Hub`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save visibility");
    } finally {
      setSavingVisibility(false);
    }
  };

  const jobActive = job && ["queued", "running"].includes(job.status);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/teamsnap/jobs?limit=5`, { withCredentials: true });
      setJobHistory(res.data.jobs || []);
    } catch { /* noop */ }
  }, []);

  const startJob = async (jobType, teamIds = []) => {
    try {
      const res = await axios.post(
        `${API}/teamsnap/jobs`,
        { job_type: jobType, team_ids: teamIds, deep_enrich: deepEnrich },
        { withCredentials: true }
      );
      if (res.data.already_running) {
        toast.info("A sync is already running — showing its progress");
      } else {
        toast.success("Sync started — it keeps running even if you leave this page");
      }
      setJob({ job_id: res.data.job_id, status: "queued", teams_done: 0, teams_total: 0, counters: {} });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start sync");
    }
  };

  const cancelJob = async () => {
    if (!job) return;
    try {
      await axios.post(`${API}/teamsnap/jobs/${job.job_id}/cancel`, {}, { withCredentials: true });
      toast.info("Stopping after the current team…");
    } catch { toast.error("Could not cancel"); }
  };

  // Poll the running job
  useEffect(() => {
    if (!job?.job_id || !["queued", "running"].includes(job.status)) return;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/teamsnap/jobs/${job.job_id}`, { withCredentials: true });
        setJob(res.data);
        if (!["queued", "running"].includes(res.data.status)) {
          const c = res.data.counters || {};
          if (res.data.status === "completed") {
            toast.success(`Sync complete — ${c.pulled || 0} new, ${c.pull_updated || 0} updated, ${c.events_imported || 0} events added`);
            setLastSync(new Date().toISOString());
          } else if (res.data.status === "failed") {
            toast.error(`Sync failed: ${res.data.current_step || "unknown error"}`);
          }
          fetchHistory();
        }
      } catch { /* keep polling */ }
    }, 2000);
    return () => clearTimeout(timer);
  }, [job, fetchHistory]);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/teamsnap/status`, { withCredentials: true });
      setStatus(res.data);
    } catch { setStatus({ connected: false }); }
    finally { setLoading(false); }
  }, []);

  const fetchTeams = useCallback(async () => {
    setTeamsLoading(true);
    try {
      const res = await axios.get(`${API}/teamsnap/teams`, { withCredentials: true });
      setTeams(res.data.teams || []);
      // Also fetch registration links
      const linksRes = await axios.get(`${API}/teamsnap/registration-links`, { withCredentials: true });
      setRegistrationLinks(linksRes.data.links || []);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to fetch teams"); }
    finally { setTeamsLoading(false); }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);
  useEffect(() => {
    if (status?.connected) {
      fetchTeams();
      fetchHistory();
      fetchVisibility();
      // Resume any sync already in flight
      axios.get(`${API}/teamsnap/jobs/active`, { withCredentials: true })
        .then((res) => { if (res.data.job) setJob(res.data.job); })
        .catch(() => {});
      // Fetch last sync time
      axios.get(`${API}/teamsnap/sync/status`, { withCredentials: true })
        .then((res) => setLastSync(res.data.last_full_sync))
        .catch(() => {});
    }
  }, [status, fetchTeams, fetchHistory, fetchVisibility]);

  const handleConnect = async () => {
    try {
      const res = await axios.get(`${API}/teamsnap/connect`, { withCredentials: true });
      window.location.href = res.data.auth_url;
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to start TeamSnap connection"); }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect TeamSnap? This removes cached data but doesn't affect your TeamSnap account.")) return;
    try {
      await axios.delete(`${API}/teamsnap/disconnect`, { withCredentials: true });
      setStatus({ connected: false });
      setTeams([]);
      toast.success("TeamSnap disconnected");
    } catch { toast.error("Failed to disconnect"); }
  };

  const fetchRoster = async (teamId) => {
    setRosterLoading((p) => ({ ...p, [teamId]: true }));
    try {
      const res = await axios.get(`${API}/teamsnap/teams/${teamId}/roster`, { withCredentials: true });
      setRosters((p) => ({ ...p, [teamId]: res.data.members || [] }));
    } catch (err) { toast.error("Failed to fetch roster"); }
    finally { setRosterLoading((p) => ({ ...p, [teamId]: false })); }
  };

  const handlePullRoster = async (teamId) => {
    await startJob("roster", [teamId]);
  };

  const toggleTeam = (teamId) => {
    if (expandedTeam === teamId) { setExpandedTeam(null); return; }
    setExpandedTeam(teamId);
    if (!rosters[teamId]) fetchRoster(teamId);
  };

  const handleSyncEvents = async () => {
    await startJob("events");
  };

  const handleFullSync = async () => {
    await startJob("both");
  };

  const handleDiscoverFields = async () => {
    setCfDiscovering(true);
    setCfResult(null);
    try {
      const res = await axios.get(`${API}/teamsnap/custom-fields/discover`, { withCredentials: true });
      setCfResult(res.data);
      if (res.data.discovered_fields?.length > 0) {
        toast.success(`Discovered ${res.data.discovered_fields.length} custom fields`);
      } else {
        toast.info("No custom fields found — they'll be captured automatically if they exist on member profiles");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Discovery failed"); }
    finally { setCfDiscovering(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-cfc-primary" /></div>;

  return (
    <div data-testid="teamsnap-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-cfc-primary">TeamSnap</h1>
          <p className="text-sm text-cfc-text-muted mt-1">Sync rosters, push leads, and manage registration links</p>
        </div>
        {status?.connected && (
          <div className="flex items-center gap-2">
            <Button variant="outline" className="border-cfc-border" onClick={fetchTeams} disabled={teamsLoading} data-testid="refresh-teams-btn">
              <RefreshCw size={14} className={`mr-1.5 ${teamsLoading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button variant="outline" className="border-red-300 text-red-500 hover:bg-red-50" onClick={handleDisconnect} data-testid="disconnect-btn">
              <Unlink size={14} className="mr-1.5" /> Disconnect
            </Button>
          </div>
        )}
      </div>

      {/* Connection Status */}
      {!status?.connected ? (
        <Card className="border-cfc-border">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-cfc-cream-alt flex items-center justify-center mx-auto mb-4">
              <Link2 size={28} className="text-cfc-primary" />
            </div>
            <h2 className="font-heading text-xl font-bold text-cfc-primary mb-2">Connect TeamSnap</h2>
            <p className="text-sm text-cfc-text-muted mb-6 max-w-md mx-auto">
              Link your TeamSnap account to sync rosters, push new signups, and share registration links on your website.
            </p>
            <Button className="bg-cfc-primary text-white" onClick={handleConnect} data-testid="connect-teamsnap-btn">
              <Link2 size={16} className="mr-2" /> Connect TeamSnap Account
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Connected status bar */}
          <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-sm" data-testid="connection-status">
            <Check size={16} className="text-green-600" />
            <span className="text-sm font-medium text-green-800">
              Connected{status.user_info?.first_name ? ` as ${status.user_info.first_name} ${status.user_info.last_name || ""}` : ""}
            </span>
            {status.connected_at && <span className="text-xs text-green-600 ml-auto">Since {new Date(status.connected_at).toLocaleDateString()}</span>}
          </div>

          {/* Parent Hub visibility */}
          <Card className="border-cfc-border" data-testid="visibility-card">
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <h2 className="font-heading text-lg font-bold text-cfc-primary">Show on Parent Hub</h2>
                  <p className="text-xs text-cfc-text-muted mt-0.5">
                    Pick which teams parents can see on the Parent Hub, schedules page, and calendar feeds. Hidden teams still sync into the CRM.
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="outline"
                    className="border-cfc-border text-xs"
                    onClick={() => saveVisibility(null)}
                    disabled={savingVisibility}
                    data-testid="show-all-teams-btn"
                  >
                    Show All
                  </Button>
                  <Button
                    className="bg-cfc-primary text-white text-xs"
                    onClick={() => saveVisibility()}
                    disabled={savingVisibility || visibleTeams === null}
                    data-testid="save-visibility-btn"
                  >
                    {savingVisibility ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Check size={14} className="mr-1.5" />}
                    Save Selection
                  </Button>
                </div>
              </div>

              {scheduleTeams.length === 0 ? (
                <p className="text-xs text-cfc-text-muted">No synced schedules yet — run a sync first, then pick your teams.</p>
              ) : (
                <>
                  <p className="text-[11px] text-cfc-text-muted mb-2">
                    {visibleTeams === null
                      ? `All ${scheduleTeams.length} teams are visible to parents`
                      : `${visibleTeams.length} of ${scheduleTeams.length} teams visible`}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {scheduleTeams.map((t) => {
                      const on = visibleTeams === null || visibleTeams.includes(t.team);
                      return (
                        <button
                          key={t.slug}
                          onClick={() => toggleVisible(t.team)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${on ? "bg-cfc-primary text-white border-cfc-primary" : "bg-white text-cfc-text-muted border-cfc-border hover:border-cfc-primary/40"}`}
                          data-testid={`visibility-toggle-${t.slug}`}
                        >
                          {on ? "✓ " : ""}{t.team}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* 2-Way Roster Sync */}
          <Card className="border-cfc-border" data-testid="full-sync-card">
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <h2 className="font-heading text-lg font-bold text-cfc-primary">2-Way Sync</h2>
                  <p className="text-xs text-cfc-text-muted mt-0.5">
                    Pulls schedules and new TeamSnap members into the CRM, pushes joined leads to TeamSnap, and flags removals.
                    Runs in the background — you can leave this page.
                    {lastSync && <span className="ml-1 text-cfc-primary font-medium">Last sync: {new Date(lastSync).toLocaleString()}</span>}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="outline"
                    className="border-cfc-border"
                    onClick={handleSyncEvents}
                    disabled={jobActive}
                    data-testid="sync-events-btn"
                  >
                    <RefreshCw size={14} className="mr-1.5" /> Schedules Only
                  </Button>
                  <Button
                    className="bg-cfc-primary text-white"
                    onClick={handleFullSync}
                    disabled={jobActive}
                    data-testid="full-sync-btn"
                  >
                    {jobActive ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <RefreshCw size={14} className="mr-1.5" />}
                    Sync Everything
                  </Button>
                </div>
              </div>

              <label className="flex items-center gap-2 text-xs text-cfc-text-muted cursor-pointer mb-1" data-testid="deep-enrich-toggle">
                <input
                  type="checkbox"
                  checked={deepEnrich}
                  onChange={(e) => setDeepEnrich(e.target.checked)}
                  disabled={jobActive}
                  className="accent-cfc-primary"
                />
                Include parent contacts &amp; custom fields (slower — uncheck for a quick roster-only sync)
              </label>

              {job && (
                <div className="mt-4 p-4 bg-cfc-cream-alt border border-cfc-border rounded-sm" data-testid="sync-progress-panel">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {jobActive
                        ? <Loader2 size={14} className="animate-spin text-cfc-primary" />
                        : job.status === "completed"
                          ? <Check size={14} className="text-green-600" />
                          : <AlertCircle size={14} className="text-red-500" />}
                      <span className="text-sm font-bold text-cfc-primary capitalize" data-testid="sync-status">{job.status}</span>
                      <span className="text-xs text-cfc-text-muted">
                        {job.current_team ? `${job.current_team} — ` : ""}{job.current_step || ""}
                      </span>
                    </div>
                    {jobActive ? (
                      <Button size="sm" variant="ghost" className="text-red-500 h-7 text-xs" onClick={cancelJob} data-testid="cancel-sync-btn">
                        Stop
                      </Button>
                    ) : (
                      <span className="text-xs text-cfc-text-muted">
                        {job.duration_seconds ? `${job.duration_seconds}s` : ""}
                      </span>
                    )}
                  </div>

                  <div className="h-1.5 w-full bg-white rounded-full overflow-hidden mb-3">
                    <div
                      className="h-full bg-cfc-primary transition-all"
                      style={{ width: `${job.teams_total ? Math.round((job.teams_done / job.teams_total) * 100) : 0}%` }}
                    />
                  </div>
                  <p className="text-[11px] text-cfc-text-muted mb-3">
                    {job.teams_done || 0} of {job.teams_total || 0} teams · {job.counters?.members_processed || 0} members processed
                  </p>

                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="full-sync-results">
                    {[
                      { label: "New Leads", value: job.counters?.pulled, cls: "bg-green-50 border-green-200 text-green-700" },
                      { label: "Updated", value: job.counters?.pull_updated, cls: "bg-blue-50 border-blue-200 text-blue-700" },
                      { label: "Pushed", value: job.counters?.pushed, cls: "bg-purple-50 border-purple-200 text-purple-700" },
                      { label: "Removals", value: job.counters?.removed, cls: "bg-orange-50 border-orange-200 text-orange-700" },
                      { label: "Events Added", value: job.counters?.events_imported, cls: "bg-white border-cfc-border text-cfc-primary" },
                      { label: "Events Updated", value: job.counters?.events_updated, cls: "bg-white border-cfc-border text-cfc-primary" },
                    ].map((stat) => (
                      <div key={stat.label} className={`p-2.5 border rounded-sm text-center ${stat.cls}`}>
                        <p className="text-lg font-bold">{stat.value || 0}</p>
                        <p className="text-[10px] font-medium uppercase opacity-80">{stat.label}</p>
                      </div>
                    ))}
                  </div>

                  {job.errors?.length > 0 && (
                    <div className="mt-3 p-2.5 bg-red-50 border border-red-200 rounded-sm" data-testid="sync-errors">
                      <p className="text-[11px] font-bold text-red-700 mb-1">{job.errors.length} team(s) had problems — the rest still synced:</p>
                      {job.errors.slice(0, 5).map((e, i) => (
                        <p key={i} className="text-[11px] text-red-600">Team {e.team_id}: {e.error}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {jobHistory.length > 0 && (
                <div className="mt-4" data-testid="sync-history">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-cfc-text-muted mb-2">Recent Syncs</p>
                  <div className="space-y-1">
                    {jobHistory.map((h) => (
                      <div key={h.job_id} className="flex items-center gap-2 text-[11px] text-cfc-text-muted" data-testid={`sync-history-${h.job_id}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${h.status === "completed" ? "bg-green-500" : h.status === "failed" ? "bg-red-500" : h.status === "cancelled" ? "bg-orange-400" : "bg-blue-400"}`} />
                        <span className="font-medium text-cfc-primary">{new Date(h.created_at).toLocaleString()}</span>
                        <span className="capitalize">{h.job_type}</span>
                        <span>· {h.teams_done}/{h.teams_total} teams</span>
                        <span>· {h.counters?.pulled || 0} new, {h.counters?.events_imported || 0} events</span>
                        <span className="capitalize">· {h.status}</span>
                        {h.errors?.length > 0 && <span className="text-red-500">· {h.errors.length} errors</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Teams */}
          <div>
            <h2 className="font-heading text-lg font-bold text-cfc-primary mb-3">Your Teams ({teams.length})</h2>
            {teamsLoading && teams.length === 0 ? (
              <div className="flex items-center gap-2 py-8 justify-center text-cfc-text-muted"><Loader2 size={18} className="animate-spin" /> Loading teams...</div>
            ) : teams.length === 0 ? (
              <Card className="border-cfc-border"><CardContent className="p-6 text-center text-sm text-cfc-text-muted">No teams found in your TeamSnap account.</CardContent></Card>
            ) : (
              <div className="space-y-2">
                {teams.map((team) => {
                  const teamId = team.id;
                  const isExpanded = expandedTeam === teamId;
                  const roster = rosters[teamId] || [];
                  const isRosterLoading = rosterLoading[teamId];
                  const link = registrationLinks.find((l) => l.team_id === teamId);

                  return (
                    <Card key={teamId} className="border-cfc-border" data-testid={`team-card-${teamId}`}>
                      <div className="flex items-center gap-3 p-4 cursor-pointer hover:bg-cfc-cream-alt/50 transition-colors" onClick={() => toggleTeam(teamId)}>
                        {isExpanded ? <ChevronDown size={16} className="text-cfc-primary" /> : <ChevronRight size={16} className="text-cfc-text-muted" />}
                        <Users size={18} className="text-cfc-primary" />
                        <div className="flex-1">
                          <span className="font-bold text-sm text-cfc-primary">{team.name}</span>
                          {team.division_name && <span className="text-xs text-cfc-text-muted ml-2">{team.division_name}</span>}
                          {team.season_name && <span className="text-xs text-cfc-text-muted ml-2">({team.season_name})</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          {link && (
                            <a href={link.teamsnap_url} target="_blank" rel="noreferrer" className="text-[11px] text-blue-600 hover:underline flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                              <ExternalLink size={10} /> TeamSnap
                            </a>
                          )}
                          <Button
                            size="sm" variant="outline"
                            className="border-cfc-primary text-cfc-primary text-[11px] h-7"
                            onClick={(e) => { e.stopPropagation(); handlePullRoster(teamId); }}
                            disabled={jobActive}
                            data-testid={`pull-roster-${teamId}`}
                          >
                            {jobActive ? <Loader2 size={12} className="animate-spin mr-1" /> : <Download size={12} className="mr-1" />}
                            Pull to CRM
                          </Button>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="border-t border-cfc-border">
                          {isRosterLoading ? (
                            <div className="p-4 text-center text-sm text-cfc-text-muted"><Loader2 size={14} className="animate-spin inline mr-1" /> Loading roster...</div>
                          ) : roster.length === 0 ? (
                            <div className="p-4 text-center text-sm text-cfc-text-muted">No members found</div>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="bg-cfc-cream-alt border-b border-cfc-border">
                                    <th className="px-4 py-2 text-left text-[11px] font-bold text-cfc-text-muted uppercase">Name</th>
                                    <th className="px-4 py-2 text-left text-[11px] font-bold text-cfc-text-muted uppercase">Position</th>
                                    <th className="px-4 py-2 text-left text-[11px] font-bold text-cfc-text-muted uppercase">Jersey</th>
                                    <th className="px-4 py-2 text-left text-[11px] font-bold text-cfc-text-muted uppercase">Role</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {roster.map((m, i) => (
                                    <tr key={m.id || i} className="border-b border-cfc-border/50 hover:bg-cfc-cream-alt/30">
                                      <td className="px-4 py-2 font-medium text-cfc-primary">{m.first_name} {m.last_name}</td>
                                      <td className="px-4 py-2 text-cfc-text-muted">{m.position || "—"}</td>
                                      <td className="px-4 py-2 text-cfc-text-muted">{m.jersey_number || "—"}</td>
                                      <td className="px-4 py-2">
                                        <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${m.is_non_player ? "bg-gray-200 text-gray-700" : "bg-cfc-primary text-white"}`}>
                                          {m.is_non_player ? "Non-Player" : "Player"}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </div>

          {/* Custom Fields Discovery */}
          {teams.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="font-heading text-lg font-bold text-cfc-primary">Custom Roster Fields</h2>
                  <p className="text-xs text-cfc-text-muted">Discover and sync custom fields from TeamSnap player profiles (uniform size, medical info, etc.)</p>
                </div>
                <Button
                  variant="outline"
                  className="border-cfc-primary text-cfc-primary"
                  onClick={handleDiscoverFields}
                  disabled={cfDiscovering}
                  data-testid="discover-fields-btn"
                >
                  {cfDiscovering ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Search size={14} className="mr-1.5" />}
                  Discover Fields
                </Button>
              </div>
              {cfResult && (
                <div className="p-3 bg-white border border-cfc-border rounded-sm" data-testid="custom-fields-result">
                  {cfResult.discovered_fields?.length > 0 ? (
                    <div>
                      <p className="text-sm font-medium text-cfc-primary mb-2">Found {cfResult.discovered_fields.length} custom field{cfResult.discovered_fields.length !== 1 ? "s" : ""}:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {cfResult.discovered_fields.map((f) => (
                          <span key={f} className="px-2 py-1 text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 rounded">{f}</span>
                        ))}
                      </div>
                      <p className="text-[11px] text-cfc-text-muted mt-2">These fields will be captured on next sync and shown on each lead&apos;s profile.</p>
                    </div>
                  ) : (
                    <p className="text-sm text-cfc-text-muted">No custom fields found on probed members. Custom fields will still be captured automatically during sync if they&apos;re available through member profile links.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Registration Links */}
          {registrationLinks.length > 0 && (
            <div>
              <h2 className="font-heading text-lg font-bold text-cfc-primary mb-3">Registration Links</h2>
              <p className="text-xs text-cfc-text-muted mb-3">Share these with parents to register directly on TeamSnap.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {registrationLinks.map((link) => (
                  <div key={link.team_id} className="flex items-center gap-3 p-3 bg-white border border-cfc-border rounded-sm" data-testid={`reg-link-${link.team_id}`}>
                    <UserPlus size={16} className="text-cfc-primary flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-cfc-primary truncate">{link.team_name}</p>
                      <a href={link.teamsnap_url} target="_blank" rel="noreferrer" className="text-[11px] text-blue-600 hover:underline truncate block">{link.teamsnap_url}</a>
                    </div>
                    <Button
                      size="sm" variant="ghost" className="text-cfc-text-muted h-7"
                      onClick={() => { navigator.clipboard.writeText(link.teamsnap_url); toast.success("Link copied"); }}
                      data-testid={`copy-link-${link.team_id}`}
                    >
                      Copy
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamSnapManager;
